import type { KnowledgeSuggestion } from "@shared/schema";
import { withCsrfHeader } from "@/lib/queryClient";

export interface SuggestionChipsProps {
  suggestions: KnowledgeSuggestion[];
  onSelect: (term: string) => void;
  containerTestId: string;
  buttonTestIdPrefix: string;
  /**
   * The search term that produced this suggestions list (i.e. the typo /
   * no-result query the user typed). Used for the click-telemetry payload
   * — when omitted, the chip still works but won't report telemetry.
   */
  originalQuery?: string;
}

/**
 * Fire-and-forget telemetry for suggestion-chip clicks. Sends a single
 * POST to `/api/knowledge/suggestion-click` so we can later analyze
 * which fuzzy suggestions actually drive recovery (vs. which queries
 * dead-end). Failure is silent — never blocks the click navigation.
 */
function reportSuggestionClick(payload: {
  originalQuery: string;
  acceptedTerm: string;
  acceptedArticleTitle: string | null;
}) {
  // `keepalive` lets the request finish even if the user immediately
  // navigates elsewhere on the same click (the chip's onSelect typically
  // re-renders the page).
  try {
    void (async () => {
      await fetch("/api/knowledge/suggestion-click", {
        method: "POST",
        credentials: "include",
        keepalive: true,
        headers: await withCsrfHeader({ "Content-Type": "application/json" }),
        body: JSON.stringify(payload),
      });
    })().catch(() => {
      /* swallow — telemetry is best-effort */
    });
  } catch {
    /* swallow */
  }
}

export function SuggestionChips({
  suggestions,
  onSelect,
  containerTestId,
  buttonTestIdPrefix,
  originalQuery,
}: SuggestionChipsProps) {
  if (suggestions.length === 0) {
    return null;
  }
  return (
    <div className="mt-2" data-testid={containerTestId}>
      <p className="text-xs text-muted-foreground">Did you mean…?</p>
      <div className="mt-1 flex flex-col gap-1">
        {suggestions.map((s) => {
          // Prefer the full article title — it reads more like a helpful
          // prompt ("How permissions and region access work") than the bare
          // matched word ("permissions"). Fall back to a search-for-term
          // string when no title is available so the chip never renders
          // empty.
          const label = s.articleTitle ?? `Search for "${s.term}" instead`;
          const tooltip = s.articleTitle
            ? `${s.articleTitle} — search for "${s.term}"`
            : `Search for "${s.term}"`;
          return (
            <button
              key={s.term}
              type="button"
              onClick={() => {
                // Report telemetry first (fire-and-forget) so we always
                // get the data point even if onSelect re-renders the page
                // and unmounts this chip mid-handler.
                if (originalQuery) {
                  reportSuggestionClick({
                    originalQuery,
                    acceptedTerm: s.term,
                    acceptedArticleTitle: s.articleTitle ?? null,
                  });
                }
                onSelect(s.term);
              }}
              className="rounded-md border bg-background px-2 py-1 text-left text-xs hover-elevate active-elevate-2"
              title={tooltip}
              data-testid={`${buttonTestIdPrefix}-${s.term}`}
            >
              <span className="line-clamp-2 break-words">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
